import * as React from 'react';
import { Popover } from '@msdyn365-commerce-modules/utilities';
import { IMailInRebateComponentProps, IMailInRebateComponentState } from './mail-in-rebate.props';
import { IRebateFullLine } from '../../../actions/DataServiceEntities.g';
import { getRebatesAsync } from '../../../actions/DataActionExtension.g';

/**
 * Mail-in rebate component.
 * @extends {React.PureComponent<IMailInRebateComponentProps>}
 */
export class MailInRebateComponent extends React.PureComponent<IMailInRebateComponentProps, IMailInRebateComponentState> {
    private popOverRef: React.RefObject<HTMLButtonElement> = React.createRef();

    constructor(props: IMailInRebateComponentProps) {
        super(props);
        this.state = { isAllRebatesListOpen: false, rebateLines: undefined };
    }

    public async componentDidMount(): Promise<void> {
        const rebateLines = await getRebatesAsync({ callerContext: this.props.context.actionContext }, [this.props.data.productId], true);
        this.setState({ rebateLines });
    }

    public render(): JSX.Element | null {
        const { displayBestOptionOnly, compactMode, resources } = this.props;
        const { rebateLines, isAllRebatesListOpen } = this.state;

        if (!rebateLines || !rebateLines.length) {
            return <></>;
        }

        let bestRebateLine: IRebateFullLine = rebateLines[0];
        let sortedRebates: IRebateFullLine[] | undefined; //this is only needed if displayBestOptionOnly is false
        if (displayBestOptionOnly) {
            bestRebateLine = rebateLines.reduce(
                (accRl, newRl) => (newRl.RebateAmount > accRl.RebateAmount ? newRl : accRl),
                rebateLines[0]
            );
        } else {
            sortedRebates = rebateLines.sort((rl1, rl2) => (rl1.RebateAmount > rl2.RebateAmount ? -1 : 1));
            bestRebateLine = sortedRebates[0];
        }
        const displayShowMoreOption = !displayBestOptionOnly && !compactMode && sortedRebates && sortedRebates.length > 0;

        return (
            <div className={`mmx-mail-in-rebate ${compactMode ? 'compact' : ''}`}>
                {this._renderRebate(bestRebateLine)}
                {displayShowMoreOption && isAllRebatesListOpen && (
                    <div
                        {...{
                            tag: Popover,
                            id: 'mailInRebatePopover',
                            className: 'mmx-mail-in-rebate-popover',
                            placement: 'bottom',
                            isOpen: isAllRebatesListOpen,
                            target: this.popOverRef
                        }}
                    >
                        {sortedRebates?.map((rl, index) => {
                            if (index !== 0) {
                                return this._renderRebate(rl);
                            }
                            return null;
                        })}
                    </div>
                )}
                {displayShowMoreOption && (
                    <a className='mmx-mail-in-rebate-popover__link-open' onClick={this.togglePopoverOpen}>
                        {isAllRebatesListOpen ? resources.showLessLinkText : resources.showMoreLinkText}
                    </a>
                )}
            </div>
        );
    }

    private _renderRebate(rebateLine: IRebateFullLine): JSX.Element | null {
        return (
            <div className='mmx-mail-in-rebate__item'>
                <div className='mmx-mail-in-rebate__item-content'>
                    {this._renderRebateLink(rebateLine)}
                    {!this.props.compactMode && this._renderExpiryDate(rebateLine)}
                </div>
            </div>
        );
    }

    private _renderRebateLink(rebateLine: IRebateFullLine): JSX.Element | null {
        const { context, resources } = this.props;

        if (!rebateLine.RebateDocLink) {
            return (
                <div className='mmx-mail-in-rebate__item-content-link'>
                    <span className='mmx-mail-in-rebate__item-content__price'>
                        {context.cultureFormatter.formatCurrency(rebateLine.RebateAmount, context.request.channel?.Currency)}
                    </span>{' '}
                    <span className='mmx-mail-in-rebate__item-content__header'>{resources.mailInRebateHeader}</span>
                </div>
            );
        }

        return (
            <a className='mmx-mail-in-rebate__item-content-link' href={rebateLine.RebateDocLink} target='_blank' rel='noopener noreferrer'>
                <span className='mmx-mail-in-rebate__item-content__price'>
                    {context.cultureFormatter.formatCurrency(rebateLine.RebateAmount, context.request.channel?.Currency)}
                </span>{' '}
                <span className='mmx-mail-in-rebate__item-content__header'>{resources.mailInRebateHeader}</span>
            </a>
        );
    }

    public togglePopoverOpen = () => {
        this.setState({ isAllRebatesListOpen: !this.state.isAllRebatesListOpen });
    };

    private _renderExpiryDate(rebateLine: IRebateFullLine): JSX.Element | null {
        if (!rebateLine.To || rebateLine.To.getTime() <= 0) {
            return null;
        }
        return <small className='mmx-mail-in-rebate__date'>{rebateLine.To.toDateString()}</small>;
    }
}
